/**
 * Los catálogos fiscales de Hacienda (Costa Rica), versión 4.4.
 *
 * El comprobante electrónico guarda **códigos**, no etiquetas: `CondicionVenta`
 * dice `01`, no «Contado». Para que la vista de la factura diga lo mismo que el
 * documento del proveedor de facturación hay que traducirlos, y esa traducción
 * es de dominio fiscal: no se inventa ni se deduce por parecido.
 *
 * ── Procedencia de estas tablas ────────────────────────────────────────────
 *
 * Se escribieron primero de memoria y **después** se cotejaron, tabla por
 * tabla, contra las fuentes oficiales:
 *
 *   · ANEXOS Y ESTRUCTURAS_V4.4 (notas 4, 5, 6, 8, 8.1, 12 y 15) y los XSD
 *     v4.4, ambos publicados en atv.hacienda.go.cr
 *   · diferencia contra el XSD v4.3, para saber qué códigos son nuevos
 *
 * Coincidieron. Dos de las filas tienen además una segunda corroboración,
 * independiente del catálogo: `CondicionVenta = 01` → «Contado» y
 * `TipoMedioPago = 04` → «Transferencia – depósito bancario» son las etiquetas
 * que imprime literalmente el PDF de GTI que está en el repositorio
 * (`docs/upgrade-2026-08/img/CB-15-2-factura-original-gti.pdf`).
 *
 * **Lo que sigue sin confirmar, y se dice en vez de rellenarse:** el valor
 * numérico de tarifa de `CodigoTarifaIVA` 10 y 11 (el nombre sugiere 0%, pero
 * es inferencia); si hay una revisión del anexo posterior a noviembre de 2024;
 * y el catálogo de UnidadMedida (~130 entradas), del que solo se confirmó
 * `Unid` = Unidad, que es la única que este negocio usa.
 *
 * **Dos erratas de los documentos oficiales que no hay que copiar:** el pie 26
 * del PDF dice «El código 06, Recaudado por terceros» cuando la tabla y los
 * cuatro XSD dicen `05`; y los XSD traen etiquetas de impuestos con errores de
 * tipeo («combustivos», sin tildes). Acá se usan las del PDF.
 *
 * **La regla de oro está en `haciendaLabel`: un código que no está en la tabla
 * se muestra tal cual.** Una etiqueta equivocada en un documento fiscal es peor
 * que un código crudo, porque el código el contador lo puede buscar y la
 * etiqueta inventada se la cree.
 *
 * Esto es distinto de `labels.ts`: aquel traduce el vocabulario del dominio de
 * Clean Blue; este transcribe un catálogo externo que no controlamos.
 */

/** Un código de catálogo que no reconocemos se muestra sin adornos. */
export function haciendaLabel(catalog: Record<string, string>, code: string | null | undefined): string | null {
  if (code == null || code === '') return null;
  return catalog[code] ?? code;
}

/** Nota 5 — condición de la venta. Los códigos 12 a 15 son nuevos en la 4.4. */
export const CONDICION_VENTA: Record<string, string> = {
  '01': 'Contado',
  '02': 'Crédito',
  '03': 'Consignación',
  '04': 'Apartado',
  '05': 'Arrendamiento con opción de compra',
  '06': 'Arrendamiento en función financiera',
  '07': 'Cobro a favor de un tercero',
  '08': 'Servicios prestados al Estado a crédito',
  '09': 'Pago de servicios prestado al Estado',
  '10': 'Venta a crédito en IVA hasta 90 días (Artículo 27, LIVA)',
  '11': 'Pago de venta a crédito en IVA hasta 90 días (Artículo 27, LIVA)',
  '12': 'Venta Mercancía No Nacionalizada',
  '13': 'Venta Bienes Usados No Contribuyente',
  '14': 'Arrendamiento Operativo',
  '15': 'Arrendamiento Financiero',
  '99': 'Otros',
};

/** Nota 6 — medio de pago. SINPE Móvil y Plataforma Digital son nuevos en 4.4. */
export const TIPO_MEDIO_PAGO: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Tarjeta',
  '03': 'Cheque',
  '04': 'Transferencia – depósito bancario',
  '05': 'Recaudado por terceros',
  '06': 'SINPE MOVIL',
  '07': 'Plataforma Digital',
  '99': 'Otros',
};

/** Nota 4 — tipo de identificación. Los códigos 05 y 06 son nuevos en la 4.4. */
export const TIPO_IDENTIFICACION: Record<string, string> = {
  '01': 'Cédula Física',
  '02': 'Cédula Jurídica',
  '03': 'DIMEX',
  '04': 'NITE',
  '05': 'Extranjero No Domiciliado',
  '06': 'No Contribuyente',
};

/**
 * Nota 8 — tipo de impuesto.
 *
 * La enumeración salta del `08` al `12`: los códigos 09, 10 y 11 **no existen**.
 */
export const CODIGO_IMPUESTO: Record<string, string> = {
  '01': 'Impuesto al Valor Agregado',
  '02': 'Impuesto Selectivo de Consumo',
  '03': 'Impuesto Único a los Combustibles',
  '04': 'Impuesto específico de Bebidas Alcohólicas',
  '05': 'Impuesto Específico sobre las bebidas envasadas sin contenido alcohólico y jabones de tocador',
  '06': 'Impuesto a los Productos de Tabaco',
  '07': 'IVA (cálculo especial)',
  '08': 'IVA Régimen de Bienes Usados (Factor)',
  '12': 'Impuesto Específico al Cemento',
  '99': 'Otros',
};

/**
 * Nota 8.1 — tarifa del IVA. En la 4.3 el elemento se llamaba `CodigoTarifa`;
 * en la 4.4 es `CodigoTarifaIVA`, y agregó los códigos 10 y 11.
 *
 * Acá va solo el nombre del catálogo: **el porcentaje no se deduce de esta
 * tabla**, se lee del `<Tarifa>` del propio comprobante, que es el que Hacienda
 * exige y el único que no puede contradecir al original.
 */
export const CODIGO_TARIFA_IVA: Record<string, string> = {
  '01': 'Tarifa 0% (Artículo 32, num 1, RLIVA)',
  '02': 'Tarifa reducida 1%',
  '03': 'Tarifa reducida 2%',
  '04': 'Tarifa reducida 4%',
  '05': 'Transitorio 0%',
  '06': 'Transitorio 4%',
  '07': 'Tarifa transitoria 8%',
  '08': 'Tarifa general 13%',
  '09': 'Tarifa reducida 0.5%',
  '10': 'Tarifa Exenta',
  '11': 'Tarifa 0% sin derecho a crédito',
};

/**
 * El nombre que lleva cada comprobante, por su elemento raíz en el XML.
 *
 * No es un catálogo numerado: es cómo se llama el documento, y es lo que va en
 * el encabezado. Una nota de crédito no se puede presentar como una factura.
 */
export const TIPO_COMPROBANTE: Record<string, string> = {
  FacturaElectronica: 'Factura Electrónica',
  NotaCreditoElectronica: 'Nota de Crédito Electrónica',
  NotaDebitoElectronica: 'Nota de Débito Electrónica',
  TiqueteElectronico: 'Tiquete Electrónico',
  FacturaElectronicaCompra: 'Factura Electrónica de Compra',
  FacturaElectronicaExportacion: 'Factura Electrónica de Exportación',
  ReciboElectronicoPago: 'Recibo Electrónico de Pago',
};

/** Nota 12 — de dónde sale el código comercial de la línea. */
export const TIPO_CODIGO_COMERCIAL: Record<string, string> = {
  '01': 'Código del producto del vendedor',
  '02': 'Código del producto del comprador',
  '03': 'Código del producto asignado por el fabricante – industriales o importadores',
  '04': 'Código uso interno',
  '99': 'Otros',
};

/**
 * La cédula con sus guiones: `3102936637` → `«3-102-936637»`.
 *
 * **Es solo para mostrar.** Lo que se guarda y lo que se escribe en un campo
 * son los dígitos pelados; el agrupamiento se pone al pintar y es reversible
 * (`replace(/-/g, '')` devuelve el original) — no agrega ni supone nada.
 *
 * Entiende los dos vocabularios que conviven en el sistema: el código de
 * Hacienda que viene en el XML (`01` física, `02` jurídica) y el `TaxIdType`
 * propio de la app (`FISICA`, `JURIDICA`), que es el que tienen a mano las
 * pantallas.
 *
 * Sin tipo, agrupa solo las dos formas que en Costa Rica no son ambiguas: 9
 * dígitos (física) y 10 que empiezan con 3 (jurídica). Cualquier otra cosa
 * —un DIMEX, un NITE, un largo raro— se devuelve tal cual: mejor sin guiones
 * que partido donde no va.
 */
export function cedulaLabel(numero: string | null | undefined, tipo?: string | null): string | null {
  if (numero == null || numero === '') return null;
  const d = numero.trim();
  if (!/^\d+$/.test(d)) return d;

  const fisica = tipo === '01' || tipo === 'FISICA';
  const juridica = tipo === '02' || tipo === 'JURIDICA';
  const sinTipo = tipo == null || tipo === '';

  if (d.length === 9 && (fisica || sinTipo)) return `${d[0]}-${d.slice(1, 5)}-${d.slice(5)}`;
  if (d.length === 10 && (juridica || (sinTipo && d[0] === '3'))) return `${d[0]}-${d.slice(1, 4)}-${d.slice(4)}`;
  return d;
}

/**
 * `2026-07-01T17:18:21-06:00` → `«01/07/2026 5:18 p.m.»`.
 *
 * Se leen los dígitos literales del texto, no un `Date`: el desfase que trae el
 * comprobante es el del emisor, y convertirlo a la zona del navegador movería la
 * hora que el documento original imprimió. El comprobante no cambia según dónde
 * se abra.
 */
export function fechaEmisionLabel(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(raw);
  if (!m) return raw;
  const [, y, mo, d, hh, mm] = m;
  const fecha = `${d}/${mo}/${y}`;
  if (hh == null || mm == null) return fecha;
  const h24 = Number(hh);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${fecha} ${h12}:${mm} ${h24 < 12 ? 'a.m.' : 'p.m.'}`;
}

/* ── El monto en letras ───────────────────────────────────────────────────── */

const UNIDADES = [
  'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
  'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete',
  'dieciocho', 'diecinueve', 'veinte', 'veintiuno', 'veintidós', 'veintitrés',
  'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve',
];
const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos',
  'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

/** 0 a 999 en letras. `apocope` convierte el «uno» final en «un» (un mil, un millón). */
function centenasEnLetras(n: number, apocope: boolean): string {
  if (n === 100) return 'cien';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];
  if (c > 0) partes.push(CENTENAS[c]!);
  if (resto > 0) {
    if (resto < 30) partes.push(UNIDADES[resto]!);
    else {
      const d = Math.floor(resto / 10);
      const u = resto % 10;
      partes.push(u === 0 ? DECENAS[d]! : `${DECENAS[d]} y ${UNIDADES[u]}`);
    }
  }
  const texto = partes.join(' ');
  // «veintiuno» apocopa con tilde («veintiún mil»); el resto, sin ella
  // («treinta y un mil»). Es la única irregularidad del apócope.
  if (!apocope) return texto;
  return texto.endsWith('veintiuno') ? texto.replace(/veintiuno$/, 'veintiún') : texto.replace(/uno$/, 'un');
}

/** Un entero en letras, hasta los miles de millones. */
export function enteroEnLetras(n: number): string {
  if (n === 0) return 'cero';

  const millones = Math.floor(n / 1_000_000);
  const miles = Math.floor((n % 1_000_000) / 1000);
  const resto = n % 1000;
  const partes: string[] = [];

  if (millones === 1) partes.push('un millón');
  else if (millones > 1) partes.push(`${enteroEnLetras(millones)} millones`);
  // «mil», no «un mil»: el uno se calla delante de mil.
  if (miles === 1) partes.push('mil');
  else if (miles > 1) partes.push(`${centenasEnLetras(miles, true)} mil`);
  if (resto > 0) partes.push(centenasEnLetras(resto, false));

  return partes.join(' ');
}

/**
 * El total en letras, como lo imprime el documento del proveedor:
 * `7345000` (céntimos) → `«setenta y tres mil cuatrocientos cincuenta COLONES.»`
 *
 * No sale del XML —Hacienda no lo pide— sino de su total, así que no puede
 * contradecirlo: es el mismo número dicho de otra forma. Los céntimos van en
 * quebrado, que es como se escriben en un documento comercial; el caso que se
 * pudo comparar contra el original es de monto redondo y coincide al carácter.
 */
export function montoEnLetras(minor: number, moneda = 'CRC'): string {
  const NOMBRE: Record<string, string> = { CRC: 'COLONES', USD: 'DÓLARES', EUR: 'EUROS' };
  const entero = Math.floor(Math.abs(minor) / 100);
  const centimos = Math.abs(minor) % 100;
  const signo = minor < 0 ? 'menos ' : '';
  const quebrado = centimos > 0 ? ` con ${String(centimos).padStart(2, '0')}/100` : '';
  return `${signo}${enteroEnLetras(entero)}${quebrado} ${NOMBRE[moneda] ?? moneda}.`;
}
